import { LessonProblem } from '@yandex-int/k-common/typings';
import b from 'b_';
import cx from 'classnames';
import { Copy, TrashClearKit } from 'education-icons';
import { Button, Link, SimpleText, ThreeDots } from 'platform-components';
import { POPUP_DIRECTIONS } from 'platform-components/constants';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import React from 'react';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { PROBLEM_SIDE_PANEL_MODE } from 'common.components/lesson-view.common/constants';
import * as actions from 'common.components/lesson-view.store/actions';
import * as selectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import { getSubjectFromPath } from 'utils/path';

import './problems__problem-controls.scss';

interface StoreProps {
    canEditLesson: boolean;
    canChangeVariant: boolean;
}

interface DispatchProps {
    setProblemSidePanelMode: typeof actions.setProblemSidePanelMode;
    removeProblemFromProblemView: typeof actions.removeProblemFromProblemViewAsync;
    setSelectedProblemId: typeof problemsViewActions.setSelectedProblemId;
}

interface OwnProps {
    problem: LessonProblem;
}

interface Props extends OwnProps, WithRouterPathProps, StoreProps, DispatchProps {}

export const ProblemControls = (props: Props) => {
    const {
        problem,
        removeProblemFromProblemView,
        setProblemSidePanelMode,
        canEditLesson,
        canChangeVariant,
        setSelectedProblemId,
    } = props;

    const dotPopupItems: JSX.Element[] = [];

    const handleDeleteProblem = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        if (problem.problem?.id) {
            removeProblemFromProblemView({ problemId: problem.problem.id });
        }
    };

    const handleChangeVariant = () => {
        if (problem.problem?.id) {
            setSelectedProblemId(problem.problem.id);
            setProblemSidePanelMode({ mode: PROBLEM_SIDE_PANEL_MODE.VARIANTS });
        }
    };

    if (canChangeVariant) {
        dotPopupItems.push(
            <Link
                key="changeVariants"
                mix="problems__problem-controls-item qa_change-problem-variants"
                onClick={handleChangeVariant}
                pseudo
            >
                <Button accent={Button.ACCENT.NORMAL} icon={<Copy />} view={Button.VIEW.CLEAR}>
                    <SimpleText id="problemView.variants" />
                </Button>
            </Link>
        );
    }

    if (canEditLesson) {
        dotPopupItems.push(
            <Link
                key="deleteProblem"
                mix={cx(
                    b('problems', 'problem-controls-item', {
                        type: 'delete',
                        theme: ThreeDots.TYPES.STRICT_WARNED,
                    }),
                    'qa_delete-problem'
                )}
                onClick={handleDeleteProblem}
                pseudo
            >
                <Button accent={Button.ACCENT.DANGER} icon={<TrashClearKit />} view={Button.VIEW.CLEAR}>
                    <SimpleText id="problemView.removeProblem" />
                </Button>
            </Link>
        );
    }

    if (dotPopupItems.length === 0) {
        return null;
    }

    return (
        <ThreeDots
            direction={POPUP_DIRECTIONS.LEFT}
            mix={b('problems', 'problem-controls')}
            popupProps={{
                mix: 'problems__problem-controls-popup',
                inLayer: false,
                fitToScreen: true,
            }}
        >
            {dotPopupItems.map((item) => (
                <ThreeDots.Item key={item.key as string} type={ThreeDots.TYPES.PLAIN}>
                    {item}
                </ThreeDots.Item>
            ))}
        </ThreeDots>
    );
};

const mapStateToProps = (storeState: any, ownProps: OwnProps): StoreProps => {
    const { problem } = ownProps;
    const problemStacks = selectors.getProblemStacks(storeState);
    const canEditLesson = lessonSelectors.getCanEditLesson(storeState);
    const subject = getSubjectFromPath();
    const canAddProblemToLesson = selectors.getCanAddProblemToLesson(storeState, { subject });
    const problemStack = problemStacks.find((stack) => stack.id === problem.from_theme) || null;
    const canChangeVariant = Boolean(
        canEditLesson && canAddProblemToLesson && problemStack && problemStack.problems_count > 1
    );

    return {
        canEditLesson,
        canChangeVariant,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            setProblemSidePanelMode: actions.setProblemSidePanelMode,
            removeProblemFromProblemView: actions.removeProblemFromProblemViewAsync,
            setSelectedProblemId: problemsViewActions.setSelectedProblemId,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ProblemControls));
